import { z } from 'zod';

/**
 * Game domain — the core entity of the game-maker product. A "game" is a
 * creation project owned by a user, produced through the template-driven
 * creation flow and (later) iterated by the game-dev backend.
 *
 * Storage: a single DynamoDB table `dgm-{env}-games` as an item collection per
 * game (decision D-034):
 *   - meta item    pk=gameId, sk='meta'           (this GameMeta)
 *   - version node pk=gameId, sk='ver#{id}'       (VersionNode — later slice)
 *   - share grant  pk=gameId, sk='share#{id}'     (ShareGrant — later slice)
 * GSIs: byOwner (ownerSub→updatedAt), byShareTarget (shareTarget→sk),
 * byPublicSlug (publicSlug). All sparse.
 */

// ─── taxonomy enums (canonical set — see ./game-taxonomy.ts) ────────────────
export const Dimensionality = z.enum(['2d', '2.5d', '3d']);
export type Dimensionality = z.infer<typeof Dimensionality>;

export const Perspective = z.enum([
  'top_down',
  'side_view',
  'first_person',
  'third_person',
  'isometric',
]);
export type Perspective = z.infer<typeof Perspective>;

export const MultiplayerModel = z.enum([
  'single',
  'local_coop',
  'online_realtime',
  'online_turn_based',
]);
export type MultiplayerModel = z.infer<typeof MultiplayerModel>;

export const VisualStyle = z.enum([
  'pixel',
  'anime',
  'lowpoly',
  'flat_vector',
  'hand_drawn',
  'realistic',
  'synthwave',
]);
export type VisualStyle = z.infer<typeof VisualStyle>;

export const GameStatus = z.enum(['draft', 'generating', 'playable', 'published', 'archived']);
export type GameStatus = z.infer<typeof GameStatus>;

/**
 * The continuous-learning capture unit — recorded for EVERY template step,
 * even when the user picks a real option. This raw trail is what the taxonomy
 * miner later uses to surface gaps and auto-propose new branches (D-034).
 */
export const TemplateAnswerSchema = z.object({
  stepId: z.string().min(1),
  /** Selected canonical option ids ([] if the user only used "other"). */
  choiceIds: z.array(z.string()).default([]),
  /** Free-text when "Other" is chosen. */
  otherText: z.string().max(1000).optional(),
  /** Follow-up clarify box — present even on a real pick. */
  clarifyText: z.string().max(1000).optional(),
  /** Named existing games the user referenced. */
  referenceGames: z.array(z.string().max(200)).max(20).optional(),
  /** Pasted reference URLs. */
  referenceUrls: z.array(z.string().max(500)).max(20).optional(),
  answeredAt: z.string().datetime(),
});
export type TemplateAnswer = z.infer<typeof TemplateAnswerSchema>;

/** Game meta item (sk='meta'). */
export const GameMetaSchema = z.object({
  gameId: z.string().min(1),
  ownerSub: z.string().min(1),
  title: z.string().min(1).max(80),
  status: GameStatus.default('draft'),
  // resolved taxonomy (denormalized from templateAnswers for fast rendering)
  dimensionality: Dimensionality.optional(),
  perspective: Perspective.optional(),
  /** Canonical genre id OR free-text from "other". */
  genre: z.string().max(200).optional(),
  multiplayer: MultiplayerModel.optional(),
  visualStyle: VisualStyle.optional(),
  visualStyleNotes: z.string().max(1000).optional(),
  /** Raw capture trail for taxonomy mining (continuous learning). */
  templateAnswers: z.array(TemplateAnswerSchema).default([]),
  /** Optional one-line objective / win-condition. */
  intentSummary: z.string().max(500).optional(),
  currentVersionId: z.string().optional(),
  /** Soft-archive (dummy-proof delete) — never DeleteItem. */
  archived: z.boolean().default(false),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type GameMeta = z.infer<typeof GameMetaSchema>;

/** Version-tree node (sk='ver#{versionId}') — schema defined now, built later. */
export const VersionNodeSchema = z.object({
  gameId: z.string().min(1),
  versionId: z.string().min(1),
  /** null/absent = root. Forms the tree. */
  parentVersionId: z.string().optional(),
  branchLabel: z.string().max(120).optional(),
  changeLabel: z.string().max(120),
  thumbnailKey: z.string().optional(),
  /** S3 key of the version's playable artifact (written by job-result, D-075). */
  artifactKey: z.string().optional(),
  /** Soft-archive; a deleted node with children becomes a tombstone (D-039). */
  archived: z.boolean().default(false),
  createdAt: z.string().datetime(),
});
export type VersionNode = z.infer<typeof VersionNodeSchema>;

/** Share grant (sk='share#{shareId}') — schema defined now, built later. */
export const ShareScope = z.enum(['game', 'version']);
export type ShareScope = z.infer<typeof ShareScope>;

export const ShareGrantSchema = z.object({
  gameId: z.string().min(1),
  shareId: z.string().min(1),
  /** friend sub | 'public' → byShareTarget GSI. */
  shareTarget: z.string().min(1),
  scope: ShareScope.default('game'),
  versionId: z.string().optional(),
  /** → byPublicSlug GSI (published URL). */
  publicSlug: z.string().optional(),
  /** Public-join unauthenticated viewers must view an ad first. */
  requiresAd: z.boolean().default(false),
  revoked: z.boolean().default(false),
  createdAt: z.string().datetime(),
});
export type ShareGrant = z.infer<typeof ShareGrantSchema>;

// ─── inputs ─────────────────────────────────────────────────────────────────

/** What the template-flow result POSTs to create a game. Server assigns
 *  gameId/ownerSub/status/timestamps. */
export const CreateGameInput = z.object({
  title: z.string().min(1).max(80),
  dimensionality: Dimensionality.optional(),
  perspective: Perspective.optional(),
  genre: z.string().max(200).optional(),
  multiplayer: MultiplayerModel.optional(),
  visualStyle: VisualStyle.optional(),
  visualStyleNotes: z.string().max(1000).optional(),
  intentSummary: z.string().max(500).optional(),
  templateAnswers: z.array(TemplateAnswerSchema).default([]),
});
export type CreateGameInput = z.infer<typeof CreateGameInput>;

/** Mutable subset an owner may PATCH. */
export const UpdateGameInput = z
  .object({
    title: z.string().min(1).max(80),
    intentSummary: z.string().max(500),
    genre: z.string().max(200),
    visualStyleNotes: z.string().max(1000),
  })
  .partial();
export type UpdateGameInput = z.infer<typeof UpdateGameInput>;
